import * as signalR from '@microsoft/signalr';
import CryptoJS from 'crypto-js';
import config from '../config';

class MessageService {
  constructor() {
    this.connection = null;
    this.encryptionKey = process.env.REACT_APP_MESSAGE_ENCRYPTION_KEY || 'your-fallback-key';
    this.callbacks = new Map();
    this.connectionState = 'disconnected';
    this.connectionPromise = null;
    this.currentUserId = null;
  }

  // Fetch message history
  async fetchMessageHistory() {
    try {
      const token = localStorage.getItem('velaToken');
      const res = await fetch(`${config.api.url}/messages/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch message history');
      
      const messages = await res.json();
      
      // Format messages to match our expected structure
      const formattedMessages = messages.map(msg => {
        let timestamp;
        try {
          // Ensure UTC timestamp
          if (msg.timestamp.endsWith('Z')) {
            timestamp = new Date(msg.timestamp).toISOString();
          } else {
            timestamp = new Date(msg.timestamp + 'Z').toISOString();
          }
        } catch (err) {
          console.error('Error parsing timestamp for message:', msg);
          timestamp = new Date().toISOString();
        }

        const formattedMsg = {
          id: msg.id,
          senderId: msg.isOutgoing ? this.currentUserId?.toString() : msg.otherPartyId?.toString(),
          senderName: msg.senderName,
          recipientId: msg.isOutgoing ? msg.otherPartyId?.toString() : this.currentUserId?.toString(),
          recipientName: msg.recipientName,
          content: msg.encryptedContent,
          timestamp: timestamp,
          read: msg.isRead,
          isOutgoing: msg.isOutgoing,
          otherPartyId: msg.otherPartyId,
          otherPartyEmail: msg.otherPartyEmail
        };
        
        return formattedMsg;
      });
      
      this.notifyListeners('messagesLoaded', formattedMessages);
      return formattedMessages;
    } catch (error) {
      console.error('Error fetching message history:', error);
      return [];
    }
  }

  // Initialize SignalR connection
  async initialize(userId, authToken) {
    this.currentUserId = userId;

    // If we're already trying to connect, wait for that attempt
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // If we're already connected, return true
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return true;
    }

    this.connectionPromise = (async () => {
      try {
        // Clean up any existing connection
        if (this.connection) {
          try {
            await this.connection.stop();
          } catch (err) {
            // Ignore stop errors
          }
        }

        this.connection = new signalR.HubConnectionBuilder()
          .withUrl(config.api.messageHubUrl, {
            accessTokenFactory: () => authToken,
            headers: {
              "Authorization": `Bearer ${authToken}`
            },
            withCredentials: false
          })
          .withHubProtocol(new signalR.JsonHubProtocol())
          .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
          .configureLogging(signalR.LogLevel.Error)
          .build();

        // Connection lifecycle handlers
        this.connection.onclose(() => {
          this.connectionState = 'disconnected';
          this.notifyListeners('connectionStateChanged', this.connectionState);
        });

        this.connection.onreconnecting(() => {
          this.connectionState = 'reconnecting';
          this.notifyListeners('connectionStateChanged', this.connectionState);
        });

        this.connection.onreconnected(async () => {
          this.connectionState = 'connected';
          this.notifyListeners('connectionStateChanged', this.connectionState);
          // Reload messages after reconnection
          await this.fetchMessageHistory();
        });

        // Set up message handlers
        this.connection.on('ReceiveMessage', this.handleIncomingMessage);
        this.connection.on('UserTyping', this.notifyListeners.bind(this, 'userTyping'));
        this.connection.on('UserStatusChanged', (userId, isOnline) => {
          this.notifyListeners('userStatusChanged', { userId, isOnline });
        });
        this.connection.on('RegistrationComplete', () => {
          this.connectionState = 'connected';
          this.notifyListeners('connectionStateChanged', this.connectionState);
        });

        // Start the connection
        await this.connection.start();
        
        // Register the user
        await this.connection.invoke('RegisterUser', userId.toString());

        // Fetch initial message history
        await this.fetchMessageHistory();

        this.connectionState = 'connected';
        this.notifyListeners('connectionStateChanged', this.connectionState);
        return true;
      } catch (err) {
        console.error('SignalR Connection Error:', err);
        this.connectionState = 'error';
        this.notifyListeners('connectionStateChanged', this.connectionState);
        this.connection = null;
        return false;
      } finally {
        this.connectionPromise = null;
      }
    })();

    return this.connectionPromise;
  }

  // Get current connection state
  getConnectionState() {
    return this.connectionState;
  }

  // Encrypt message
  encryptMessage(message) {
    return CryptoJS.AES.encrypt(JSON.stringify(message), this.encryptionKey).toString();
  }

  // Decrypt message
  decryptMessage(encryptedMessage) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedMessage, this.encryptionKey);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (err) {
      console.error('Decryption Error:', err);
      return null;
    }
  }

  // Send a message
  async sendMessage(recipientId, content) {
    if (!this.connection?.state === signalR.HubConnectionState.Connected) {
      console.error('Cannot send message: No active SignalR connection');
      return false;
    }

    try {
      // Send via SignalR and let the server assign the UTC timestamp
      await this.connection.invoke('SendMessage', recipientId.toString(), content);
      return true;
    } catch (err) {
      console.error('Send Message Error:', err);
      return false;
    }
  }

  // Handle incoming message
  handleIncomingMessage = (message) => {
    if (!message) {
      console.error('Received null message');
      return;
    }
    
    // Ensure UTC timestamp
    let timestamp;
    try {
      // If we receive a timestamp, ensure it's UTC
      if (message.timestamp || message.Timestamp) {
        const rawTimestamp = message.timestamp || message.Timestamp;
        
        // Parse the timestamp and ensure it's UTC
        if (rawTimestamp.endsWith('Z')) {
          // Already UTC, just parse and format
          timestamp = new Date(rawTimestamp).toISOString();
        } else {
          // Treat as UTC even if not marked as such
          const utcDate = new Date(rawTimestamp + 'Z');
          timestamp = utcDate.toISOString();
        }
      } else {
        // If no timestamp, use current UTC time
        timestamp = new Date().toISOString();
      }
    } catch (err) {
      console.error('Error parsing timestamp:', err);
      timestamp = new Date().toISOString();
    }
    
    const formattedMessage = {
      senderId: message.senderId || message.SenderId,
      recipientId: message.recipientId || message.RecipientId,
      content: message.content || message.EncryptedContent,
      timestamp: timestamp,
      read: message.read || message.IsRead || false
    };

    this.notifyListeners('messageReceived', formattedMessage);
  };

  // Send typing indicator
  async sendTypingIndicator(recipientId) {
    if (!this.connection?.state === signalR.HubConnectionState.Connected) {
      console.error('Cannot send typing indicator: No active SignalR connection');
      return;
    }

    try {
      await this.connection.invoke('UserTyping', recipientId.toString());
    } catch (err) {
      console.error('Typing Indicator Error:', err);
    }
  }

  // Add event listener
  on(event, callback) {
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, new Set());
    }
    this.callbacks.get(event).add(callback);
  }

  // Remove event listener
  off(event, callback) {
    if (this.callbacks.has(event)) {
      this.callbacks.get(event).delete(callback);
    }
  }

  // Notify all listeners of an event
  notifyListeners(event, data) {
    if (this.callbacks.has(event)) {
      this.callbacks.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error('Error in listener callback:', err);
        }
      });
    }
  }

  // Disconnect
  async disconnect() {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.connectionState = 'disconnected';
      this.notifyListeners('connectionStateChanged', this.connectionState);
    }
  }

  // Format message timestamp for display
  formatMessageTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return timestamp;
    }
  };
}

export const messageService = new MessageService(); 